import { XV2 } from './XV2';
import { useImmer } from 'use-immer';
import { useEffectOnce } from 'usehooks-ts';
import { toastShowError } from '@orderx/lab/xr/toast';

export function useXV2<URL extends keyof XV2.API_IO, I extends XV2.API_IO[URL][0], O extends XV2.API_IO[URL][1]>(
    input: { url: URL },
    options?: { onSuccess?(result: O); onError?(error); onFinally?() },
) {
    const [state, setState] = useImmer<{
        isLoading?: boolean;
        requestParams?: I;
        result?: O;
        errorMessage?: string;
    }>({});

    async function run(i: I): Promise<O> {
        const requestParams = { ...i };

        setState((state) => {
            state.requestParams = requestParams as any;
            state.isLoading = true;
        });

        try {
            const result = await XV2.req(input.url, requestParams);

            setState((state) => {
                state.result = result;
                state.errorMessage = undefined;
            });

            await options?.onSuccess?.(result);

            return result;
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message;

            setState((state) => {
                state.result = undefined;
                state.errorMessage = errorMessage;
            });

            if (options?.onError) {
                await options.onError(error?.response?.data || error);
            } else if (error.response?.data?.message) {
                toastShowError(errorMessage);
            }

            throw error;
        } finally {
            setState((state) => {
                state.requestParams = undefined;
                state.isLoading = false;
            });
        }
    }

    return {
        ...state,
        run: run,
        loading: state.isLoading,
        useRunOnInit(input: I) {
            useEffectOnce(() => {
                run(input);
            });

            return { ...state, run: run };
        },
    };
}
